import React, { useEffect, useRef, useState, useCallback } from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { extractVideoId } from '../../services/youtubeService';
import { buildDriveStreamUrl } from '../../utils/driveStreamUrl';

/**
 * Unified Shorts Player — supports both YouTube and Google Drive sources
 *
 * Architecture:
 *   YouTube shorts → Plyr YouTube provider (iframe API)
 *   Drive clips    → Plyr HTML5 <video> provider (via Supabase Edge Function proxy)
 *
 * Source detection:
 *   shortData.driveFileId → Drive mode (HTML5 <video>)
 *   shortData.shortUrl    → YouTube mode (iframe API)
 */

// Detect video source from shortData
function detectSource(shortData) {
  if (!shortData) return null;
  if (shortData.driveFileId) return 'drive';
  if (shortData.shortUrl) return 'youtube';
  return null;
}

const ShortsPlayerUnified = ({
  shortData,
  isPlaying,
  isMuted,
  onPlayStateChange,
  onMuteStateChange,
  onNextVideo,
  onPrevVideo,
  onFirstInteraction,
  canAutoplayUnmuted,
  autoplayDetected
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const progressBarRef = useRef(null);
  const wasPlayingBeforeDragRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverTime, setHoverTime] = useState(0);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [actualMuteState, setActualMuteState] = useState(isMuted);
  const [showUnmuteHint, setShowUnmuteHint] = useState(false);

  const source = detectSource(shortData);
  const videoId = source === 'youtube' ? extractVideoId(shortData?.shortUrl) : null;
  const driveFileId = source === 'drive' ? shortData?.driveFileId : null;

  // Stable source key for re-initialization
  const sourceKey = source === 'youtube' ? videoId : driveFileId;

  // ===== PROGRESS TRACKING =====
  const progressIntervalRef = useRef(null);

  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) return;
    progressIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.duration) {
        const pct = (playerRef.current.currentTime / playerRef.current.duration) * 100;
        setProgress(pct);
      }
    }, 100);
  }, []);

  // ===== YOUTUBE VOLUME POLLING (only needed for YouTube) =====
  const volumePollingRef = useRef(null);

  const stopVolumePolling = useCallback(() => {
    if (volumePollingRef.current) {
      clearInterval(volumePollingRef.current);
      volumePollingRef.current = null;
    }
  }, []);

  const startVolumePolling = useCallback(() => {
    // Only needed for YouTube — HTML5 video doesn't need polling
    if (source !== 'youtube') return;
    if (volumePollingRef.current) return;

    volumePollingRef.current = setInterval(() => {
      if (playerRef.current?.embed) {
        try {
          const yt = playerRef.current.embed;
          if (typeof yt.isMuted === 'function') {
            const muted = yt.isMuted();
            if (muted !== actualMuteState) {
              setActualMuteState(muted);
            }
          }
        } catch (_) { /* silent */ }
      }
    }, 500);
  }, [source, actualMuteState]);

  // ===== INITIALIZE PLYR =====
  useEffect(() => {
    if (!videoRef.current || !sourceKey) return;

    let waitForYTPlayer;

    const timer = setTimeout(() => {
      // Destroy previous instance
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) {}
      }

      if (!videoRef.current) return;

      try {
        // ----- Plyr config (shared base) -----
        const baseConfig = {
          controls: [],          // Hide all Plyr controls — we use custom UI
          clickToPlay: false,
          hideControls: false,
          resetOnEnd: false,
          autoplay: true,
          muted: true,           // Start muted for reliable autoplay
          volume: 1,
          speed: { selected: 1, options: [1] },
          loop: { active: false },
          fullscreen: { enabled: false, fallback: false },
          storage: { enabled: false },
          disableContextMenu: true,
          ratio: '9:16',
        };

        // ----- YouTube-specific config -----
        if (source === 'youtube') {
          baseConfig.youtube = {
            noCookie: true,
            rel: 0,
            showinfo: 0,
            iv_load_policy: 3,
            modestbranding: 1,
            customControls: true,
            controls: 0,
            fs: 0,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.origin,
            mute: 1,
          };
        }

        const player = new Plyr(videoRef.current, baseConfig);
        playerRef.current = player;

        // ----- Common events (both providers) -----
        player.on('ready', () => {
          console.log(`[Unified] ${source} player ready:`, sourceKey);
          setIsReady(true);
          setError(null);

          if (source === 'youtube') {
            // YouTube needs special init — wait for embed API
            waitForYTPlayer = setInterval(() => {
              if (player.embed && typeof player.embed.playVideo === 'function') {
                try {
                  const state = player.embed.getPlayerState?.() ?? -1;
                  if (state === -1 || state === undefined) return;
                  clearInterval(waitForYTPlayer);

                  if (isPlaying) player.embed.playVideo();

                  const shouldMute = isMuted || (!canAutoplayUnmuted && autoplayDetected);
                  if (shouldMute) {
                    player.embed.mute();
                    setActualMuteState(true);
                    if (!isMuted && !canAutoplayUnmuted && autoplayDetected) {
                      setShowUnmuteHint(true);
                      setTimeout(() => setShowUnmuteHint(false), 3000);
                    }
                  } else {
                    player.embed.unMute();
                    player.embed.setVolume(75);
                    setActualMuteState(false);
                  }
                } catch (_) {}
              }
            }, 100);
            setTimeout(() => clearInterval(waitForYTPlayer), 3000);

          } else {
            // Drive / HTML5 — straightforward init
            setActualMuteState(true); // Start muted
          }
        });

        player.on('play', () => {
          onPlayStateChange(true);
          startProgressTracking();
          if (source === 'youtube') {
            setTimeout(() => startVolumePolling(), 500);
          }
        });

        player.on('pause', () => {
          onPlayStateChange(false);
          stopProgressTracking();
        });

        player.on('ended', () => {
          onPlayStateChange(false);
          stopProgressTracking();
          setTimeout(() => onNextVideo?.(), 100);
        });

        player.on('error', (event) => {
          console.error(`[Unified] ${source} error:`, event);
          setError('Video unavailable');
          setIsReady(false);
        });

        player.on('timeupdate', () => {
          if (player.duration) {
            setProgress((player.currentTime / player.duration) * 100);
            setDuration(player.duration);
          }
        });

        player.on('loadedmetadata', () => {
          setDuration(player.duration || 0);
        });

      } catch (err) {
        console.error('[Unified] Init failed:', err);
        setError('Failed to load video player');
        setIsReady(false);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (waitForYTPlayer) clearInterval(waitForYTPlayer);
      stopProgressTracking();
      stopVolumePolling();
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) {}
        playerRef.current = null;
      }
    };
  }, [sourceKey, source]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== PLAY / PAUSE from parent =====
  useEffect(() => {
    if (!playerRef.current || !isReady) return;
    const player = playerRef.current;

    if (source === 'youtube' && player.embed?.playVideo) {
      try {
        const state = player.embed.getPlayerState();
        const playing = state === 1;
        if (isPlaying && !playing) player.embed.playVideo();
        else if (!isPlaying && playing) player.embed.pauseVideo();
        return;
      } catch (_) {}
    }

    // HTML5 / fallback
    try {
      if (isPlaying && player.paused) {
        const p = player.play();
        if (p?.catch) p.catch(() => {});
      } else if (!isPlaying && !player.paused) {
        player.pause();
      }
    } catch (_) {}
  }, [isPlaying, isReady, source]);

  // ===== MUTE / UNMUTE from parent =====
  useEffect(() => {
    if (!playerRef.current || !isReady) return;
    const player = playerRef.current;

    if (source === 'youtube') {
      // YouTube path — use embed API
      if (player.embed?.mute) {
        try {
          if (isMuted) {
            player.embed.mute();
          } else {
            player.embed.unMute();
            player.embed.setVolume?.(75);
          }
          setActualMuteState(isMuted);
          return;
        } catch (_) {}
      }
    }

    // HTML5 / Drive path — direct property (simple!)
    try {
      player.muted = isMuted;
      if (!isMuted) player.volume = 0.75;
      setActualMuteState(isMuted);
    } catch (_) {}
  }, [isMuted, isReady, source]);

  // ===== CLEANUP =====
  useEffect(() => {
    return () => {
      stopProgressTracking();
      stopVolumePolling();
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (_) {}
      }
    };
  }, [stopProgressTracking, stopVolumePolling]);

  // ===== PROGRESS BAR INTERACTION =====
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleProgressMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!playerRef.current || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(1, Math.min(99, (x / rect.width) * 100));
    playerRef.current.currentTime = (pct / 100) * duration;
    setProgress(pct);
    setIsDragging(true);

    wasPlayingBeforeDragRef.current = isPlaying;
    if (isPlaying) onPlayStateChange(false);

    const onMove = (me) => {
      const mx = Math.max(0, Math.min(me.clientX - rect.left, rect.width));
      const mp = Math.max(1, Math.min(99, (mx / rect.width) * 100));
      playerRef.current.currentTime = (mp / 100) * duration;
      setProgress(mp);
    };
    const onUp = () => {
      setIsDragging(false);
      if (wasPlayingBeforeDragRef.current) onPlayStateChange(true);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Touch support for progress bar
  useEffect(() => {
    const bar = progressBarRef.current;
    if (!bar) return;

    let dragging = false;

    const onStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      setIsDragging(true);
      wasPlayingBeforeDragRef.current = isPlaying;
      if (isPlaying) onPlayStateChange(false);

      const touch = e.touches[0];
      const rect = bar.getBoundingClientRect();
      const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
      const pct = Math.max(1, Math.min(99, (x / rect.width) * 100));
      if (playerRef.current && duration) {
        playerRef.current.currentTime = (pct / 100) * duration;
        setProgress(pct);
      }
    };
    const onMove = (e) => {
      if (!dragging || !playerRef.current || !duration) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rect = bar.getBoundingClientRect();
      const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
      const pct = Math.max(1, Math.min(99, (x / rect.width) * 100));
      playerRef.current.currentTime = (pct / 100) * duration;
      setProgress(pct);
    };
    const onEnd = (e) => {
      if (!dragging) return;
      e.preventDefault();
      dragging = false;
      setIsDragging(false);
      if (wasPlayingBeforeDragRef.current) onPlayStateChange(true);
    };

    bar.addEventListener('touchstart', onStart, { passive: false });
    bar.addEventListener('touchmove', onMove, { passive: false });
    bar.addEventListener('touchend', onEnd, { passive: false });
    return () => {
      bar.removeEventListener('touchstart', onStart);
      bar.removeEventListener('touchmove', onMove);
      bar.removeEventListener('touchend', onEnd);
    };
  }, [duration, isPlaying, onPlayStateChange]);

  // ===== HOVER TIME TOOLTIP =====
  const handleProgressMouseMove = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setHoverPosition(x);
    setHoverTime((x / rect.width) * duration);
  };

  // ===== RENDER =====
  if (!shortData || !sourceKey) {
    return (
      <div className="shorts-player-error">
        <p>No video data available</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shorts-player-error">
        <p>{error}</p>
        <p>Video may be unavailable</p>
      </div>
    );
  }

  return (
    <div className="shorts-player-container">
      <div
        className={`shorts-player ${!isPlaying ? 'paused' : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Video player"
      >
        <div className="plyr-player">
          {/* ===== SOURCE-SPECIFIC ELEMENT ===== */}
          {source === 'youtube' ? (
            // YouTube: Plyr YouTube provider
            <div
              ref={videoRef}
              data-plyr-provider="youtube"
              data-plyr-embed-id={videoId}
            />
          ) : (
            // Drive: HTML5 <video> via stream proxy
            <video
              ref={videoRef}
              playsInline
              preload="auto"
              crossOrigin="anonymous"
            >
              <source
                src={buildDriveStreamUrl(driveFileId)}
                type="video/mp4"
              />
            </video>
          )}

          {/* Loading state */}
          {!isReady && !error && (
            <div className="player-placeholder">
              <div className="loading-spinner"></div>
              <p>Loading video...</p>
            </div>
          )}
        </div>

        {/* Progress bar (works identically for both providers) */}
        <div
          ref={progressBarRef}
          className={`video-progress-bar ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleProgressMouseDown}
          onMouseMove={handleProgressMouseMove}
          onMouseLeave={() => { setHoverTime(0); setHoverPosition(0); }}
        >
          <div
            className="video-progress-fill"
            style={{ '--progress-percent': `${progress}%` }}
          />
          <div
            className="video-progress-handle"
            style={{ left: `${progress}%` }}
          />
          {hoverTime > 0 && (
            <div
              className="video-progress-tooltip"
              style={{ left: `${hoverPosition}px` }}
            >
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        {/* Unmute hint (autoplay policy) */}
        {showUnmuteHint && (
          <div className="unmute-hint">
            <div className="unmute-hint-content">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" style={{ marginRight: '8px' }}>
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
              <span>Tap to unmute</span>
            </div>
          </div>
        )}

        {/* Audio control */}
        <div className="audio-control-wrapper">
          <div
            className={`audio-control-btn ${!actualMuteState ? 'unmuted' : ''}`}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFirstInteraction?.();
              onMuteStateChange(!isMuted);
              if (isMuted && showUnmuteHint) setShowUnmuteHint(false);
            }}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {actualMuteState ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
          </div>
        </div>

        {/* Navigation controls overlay (hover to show on desktop) */}
        <div className="video-nav-controls">
          <button
            className="nav-control nav-prev"
            onClick={(e) => { e.stopPropagation(); onPrevVideo?.(); }}
            aria-label="Previous video"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </button>
          <button
            className="nav-control nav-play-pause"
            onClick={(e) => { e.stopPropagation(); onPlayStateChange?.(!isPlaying); }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          <button
            className="nav-control nav-next"
            onClick={(e) => { e.stopPropagation(); onNextVideo?.(); }}
            aria-label="Next video"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShortsPlayerUnified;
